import { useEffect } from 'react';
import { Pagination, Collapse, Button, Tag } from 'antd';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { useLocation } from 'react-router-dom';
import BulkTransactionInfoSearchForm from './BulkTransactionInfoSearchForm';
import getStatus from '@utils/getStatus';
import { dowloadOriginalBulkFileApi, getBulkTransactionInfoApi } from '@http/bulkTransaction';
import { CheckCircleOutlined, CloseCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import digitNumber from '@utils/digitNumber';
import { useTranslation } from 'react-i18next';
import BulkTransactionActionModal from './BulkTransactionActionModal';
import {
  actionModalTypeAC,
  bulkTransactionActionModalOpenAC,
  pickedBulkTransactionInfoAC,
} from '@store/reducers/bulkTransaction';
import getBankName from '@utils/bankName';
import operationType from '@utils/operationType';

export const BulkTransactionInfo = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const pickedBulkTransactionInfo = useAppSelector(state => state.bulkTransactionReducer.pickedBulkTransactionInfo);
  const pickedBulkTransactionFileId = location.pathname.slice(20);

  const changePage = (page: any) => {
    dispatch(getBulkTransactionInfoApi({ file_id: pickedBulkTransactionFileId }, page));
  };

  useEffect(() => {
    if (String(pickedBulkTransactionInfo?.file_info?.file_id) !== pickedBulkTransactionFileId) {
      dispatch(getBulkTransactionInfoApi({ file_id: pickedBulkTransactionFileId }, 1));
    }

    return () => {
      dispatch(pickedBulkTransactionInfoAC({}));
    };
  }, []);

  return (
    <div>
      <div className='md:grid grid-cols-3 gap-5 my-7'>
        <div className='p-5 bg-[#f9f9f9] rounded-lg text-sm leading-8 text-slate-600'>
          <p>{t('Total amount')}</p>
          <p className='text-2xl font-semibold text-black'>
            {digitNumber(pickedBulkTransactionInfo?.file_info?.total_amount)}{' '}
            {pickedBulkTransactionInfo?.file_info?.trn_ccy}
          </p>
          <p className='flex justify-between'>
            <span>{t('Between own accounts')}: </span>
            <span className='font-semibold'>{pickedBulkTransactionInfo?.file_info?.own_trn_count}</span>
          </p>
          <p className='flex justify-between'>
            <span>{t('Intrabank payments')}: </span>
            <span className='font-semibold'>{pickedBulkTransactionInfo?.file_info?.internal_trn_count}</span>
          </p>
          <p className='flex justify-between'>
            <span>{t('Interbank payments')}: </span>
            <span className='font-semibold'>{pickedBulkTransactionInfo?.file_info?.interbank_trn_count}</span>
          </p>
          <div className='flex mt-5'>
            <Button
              type='primary'
              size='middle'
              onClick={() => dispatch(dowloadOriginalBulkFileApi({ file_id: pickedBulkTransactionFileId }))}
            >
              {t('Download file')}
            </Button>
          </div>
        </div>
        <div className='p-5 bg-[#f9f9f9] rounded-lg text-sm leading-8 text-slate-600'>
          <p>{t('Transactions')}</p>
          <p className='text-2xl font-semibold text-black'>{pickedBulkTransactionInfo?.file_info?.trn_count}</p>
          <div className='mt-3 grid grid-cols-2 gap-3'>
            <div className='h-18 p-2 bg-[#f0fdf4] flex items-center gap-3 rounded'>
              <CheckCircleOutlined style={{ color: 'green', fontSize: '20px' }} />
              <div className='leading-6'>
                <p className='text-sm'>{t('Successful count')}</p>
                <p className='text-xl font-semibold text-black'>
                  {pickedBulkTransactionInfo?.file_info?.success_count}
                </p>
              </div>
            </div>

            <div className='h-18 p-2 bg-[#fef2f2] flex items-center gap-3 rounded'>
              <CloseCircleOutlined className='cursor-pointer' style={{ color: 'red', fontSize: '20px' }} />
              <div className='leading-6'>
                <p className='text-sm'>{t('Not successful count')}</p>
                <p className='text-xl font-semibold text-black'>{pickedBulkTransactionInfo?.file_info?.fail_count}</p>
              </div>
            </div>
          </div>
          <div className='mt-5 flex justify-end'>
            {pickedBulkTransactionInfo?.file_info?.is_authenticator &&
            pickedBulkTransactionInfo?.file_info?.status === 'TO_APPROVE' ? (
              <div>
                <Button
                  type='primary'
                  danger
                  className='ml-3'
                  size='middle'
                  onClick={() => {
                    dispatch(bulkTransactionActionModalOpenAC(true));
                    dispatch(actionModalTypeAC('DECLINE'));
                  }}
                >
                  {t('Reject')}
                </Button>
                <Button
                  className='ml-3'
                  type='primary'
                  size='middle'
                  onClick={() => {
                    dispatch(bulkTransactionActionModalOpenAC(true));
                    dispatch(actionModalTypeAC('APPROVE'));
                  }}
                >
                  {t('Confirm')}
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        <div className='p-5 bg-[#f9f9f9] rounded text-sm leading-6 text-slate-600'>
          <p>{t('File information')}</p>
          <div className='flex items-center gap-3 mb-2'>
            <FileTextOutlined style={{ color: 'black', fontSize: '18px', fontWeight: 'bold' }} />
            <span>{getStatus(pickedBulkTransactionInfo?.file_info?.status, 12, t)}</span>
          </div>
          <span>{t('Date of creation')}:</span>
          <p className='font-semibold mb-2'>{pickedBulkTransactionInfo?.file_info?.created_at}</p>
          <span>{t('Initiator')}:</span>
          <p className='font-semibold'>{pickedBulkTransactionInfo?.file_info?.initiator}</p>

          <div className='mt-2'>
            {pickedBulkTransactionInfo?.file_info?.authenticator.length > 0 ? (
              <div className=''>
                <p className='mr-3'>{t('Authorizers')}: </p>
                {pickedBulkTransactionInfo?.file_info.authenticator.map((item: any) => (
                  <div className='flex items-center' key={item.authenticator_id}>
                    <p className='mr-3 font-semibold'>{item.authenticator_name}</p>
                    {item.authed === true ? <Tag color='success'>{t('Approved')}</Tag> : null}
                    {item.authed === false ? <Tag color='error'>{t('Rejected')}</Tag> : null}
                    {item.authed === null ? <Tag color='processing'>{t('Signing in')}</Tag> : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className='mt-2'>
            {pickedBulkTransactionInfo?.file_info?.error_message?.length > 0 ? (
              <div>
                {pickedBulkTransactionInfo?.file_info?.error_message?.map((item: any, index: any) => (
                  <div>
                    {item !== '' ? (
                      <div className='text-red-400'>
                        {index + 1} {item}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <p className='my-5 text-lg font-semibold color-active'>{t('Transactions')}</p>
      <div className='my-5'>
        <BulkTransactionInfoSearchForm />
      </div>

      <div className='tableHeader grid grid-cols-12 pb-5 p-5 pl-12 gap-3' style={{ borderBottom: '1px solid #eee' }}>
        <p className='col-span-1 max-lg:col-span-2'>{t('Date')}</p>
        <p className='col-span-2'>{t('Recipient')}</p>
        <p className='col-span-2 max-lg:col-span-3'>{t('Sender account')}</p>
        <p className='col-span-2 max-lg:hidden'>{t('Recipient account')}</p>
        <p className='col-span-1'>{t('Payment type')}</p>
        <p className='col-span-2 text-right'>{t('Amount')}</p>
        <p className='col-span-2 text-right'>{t('Status')}</p>
      </div>

      {pickedBulkTransactionInfo?.results?.length > 0 ? (
        <>
          <Collapse
            ghost
            className='p-0'
            items={pickedBulkTransactionInfo?.results?.map((item: any) => ({
              key: item.id,
              label: (
                <div
                  style={{ borderBottom: '1px solid #eee' }}
                  className='grid grid-cols-12 items-center py-5 border-b cursor-pointer gap-3 text-sm'
                  key={item.id}
                >
                  <p className='col-span-1 max-lg:col-span-2'>{item.created_at.slice(0, 10)}</p>
                  <p className='col-span-2'>{item.receiver_name}</p>
                  <p className='col-span-2 max-lg:col-span-3'>{item.sending_ac}</p>
                  <p className='col-span-2 max-lg:hidden'>{item.receiving_ac}</p>
                  <p className='col-span-1'>{item.type}</p>
                  <p className='col-span-2 text-right'>
                    {digitNumber(item.sending_amount)} {item.sending_ccy}
                  </p>
                  <p className='col-span-2 text-right'>{getStatus(item.status, 12, t)}</p>
                </div>
              ),
              children: (
                <div className='bg-slate-50'>
                  <div className='grid grid-cols-7 items-center py-5 px-2 border-b max-md:text-ex lg:gap-5'>
                    <div className='col-span-1' />
                    <div className='col-span-6 leading-6 lg:leading-8'>
                      <div className='grid grid-cols-6'>
                        <p>{t('TxID')}: </p>
                        <p className='ml-3 font-semibold col-span-5'>{item.trans_id}</p>
                      </div>
                      <div className='grid grid-cols-6'>
                        <p>{t('Transaction type')}: </p>
                        <p className='ml-3 font-semibold col-span-5'>{operationType(item.type, t)}</p>
                      </div>
                      <div className='grid grid-cols-6'>
                        <p>{t('Date of creation')}: </p>
                        <p className='ml-3 font-semibold'>{item.created_at.slice(0, 19)}</p>
                      </div>
                      <div className='grid grid-cols-6'>
                        <p>{t('Sender account')}: </p>
                        <p className='ml-3 font-semibold col-span-5'>
                          {item.sending_ac} ({item.sending_ccy})
                        </p>
                      </div>
                      <div className='grid grid-cols-6'>
                        <p>{t('Recipient')}: </p>
                        <p className='ml-3 font-semibold col-span-5'>{item.receiver_name}</p>
                      </div>
                      <div className='grid grid-cols-6'>
                        <p>{t('Recipient TIN')}: </p>
                        <p className='ml-3 font-semibold col-span-5'>{item.receiver_tax_number}</p>
                      </div>
                      <div className='grid grid-cols-6'>
                        <p>{t('Recipient account')}: </p>
                        <p className='ml-3 font-semibold col-span-5'>{item.receiving_ac}</p>
                      </div>

                      {item.type === 'INTERBANK' ? (
                        <div>
                          <div className='grid grid-cols-6'>
                            <p>{t('Beneficiary Bank')}: </p>
                            <p className='ml-3 font-semibold col-span-5'>{getBankName(item.receiver_bank_bic)}</p>
                          </div>
                          <div className='grid grid-cols-6'>
                            <p>{t('BIC')}: </p>
                            <p className='ml-3 font-semibold col-span-5'>{item.receiver_bank_bic}</p>
                          </div>
                        </div>
                      ) : null}
                      <div className='grid grid-cols-6'>
                        <p>{t('Amount')}: </p>
                        <p className='ml-3 font-semibold col-span-5'>
                          {digitNumber(item.sending_amount)} {item.sending_ccy}
                        </p>
                      </div>
                      <div className='grid grid-cols-6'>
                        <p>{t('Narrative')}: </p>
                        <p className='ml-3 font-semibold col-span-5'>{item.narrative}</p>
                      </div>
                      <div className='grid grid-cols-6'>
                        <p>{t('Status')}: </p>
                        <p className='ml-3 font-semibold col-span-5'>{getStatus(item.status, 12, t)}</p>
                      </div>
                      {!item.valid ? (
                        <div className='grid grid-cols-6'>
                          <p>{t('Error')}: </p>
                          <p className='ml-3 font-semibold col-span-5'>{item.error_message}</p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ),
            }))}
          />
          <div className='mt-7 flex justify-end'>
            <Pagination
              onChange={changePage}
              current={pickedBulkTransactionInfo.current_page}
              defaultPageSize={10}
              total={pickedBulkTransactionInfo.total_pages * 10}
              showSizeChanger={false}
            />
          </div>
        </>
      ) : (
        <div className='h-24 lg:h-72 flex items-center justify-center'>
          <p>{t('No Transactions')}</p>
        </div>
      )}
      <BulkTransactionActionModal fileId={pickedBulkTransactionFileId} />
    </div>
  );
};

export default BulkTransactionInfo;
